/* eslint-disable max-len */
/* eslint-disable react/prop-types */
/* eslint-disable object-curly-newline */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Modal, Box } from '@mui/material';
import {
  Calendar, AlertTriangle, RefreshCw, ExternalLink,
} from 'lucide-react';
import { formatDate } from '../../../utils/time';
import AlertDescription from '../partials/AlertDescription';
import StatusPill from '../../../components/StatusPill';
import { sendRequest } from '../../../api/shootAPI';
import API from '../../../api/endpoints';
import { UserContext } from '../../../contexts/User';

const STATUS_TONE = {
  Pending: 'warning',
  Resolved: 'success',
  AcknowledgementSent: 'info',
  Acknowledged: 'info',
  Error: 'error',
};

const initials = (u) => `${(u?.firstName || '').slice(0, 1)}${(u?.lastName || '').slice(0, 1)}`.toUpperCase() || '?';

function ViewIssueModal({ selectedAlert, isOpen, handleClose }) {
  const { member } = useContext(UserContext);
  const [alert, setAlert] = useState(selectedAlert);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => { setAlert({ ...selectedAlert }); }, [selectedAlert]);

  const handleSendAcknowledgement = async () => {
    setIsUpdating(true);
    const response = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.sendAcknowledgement(selectedAlert?.id),
      data: { managerId: member.id },
    });
    setIsUpdating(false);
    if (response.success) setAlert({ ...alert, status: 'AcknowledgementSent' });
  };

  const alertActionText = (status) => {
    switch (status) {
      case 'Pending': return 'Please send an acknowledgement to resolve this alert.';
      case 'AcknowledgementSent': return 'Acknowledgement has been sent to the member.';
      case 'Resolved': return 'This alert has been resolved.';
      default: return '';
    }
  };

  if (!isOpen) return null;

  const u = alert?.member?.user;

  return (
    <Modal open onClose={handleClose} className="flex-row align-c jstfy-c" data-testid="view-issue-modal">
      <Box style={{ width: 586, maxWidth: 'calc(100% - 32px)', maxHeight: '90vh', overflowY: 'auto', padding: 24, background: '#fff', borderRadius: 12 }}>
        {/* Header: status + date */}
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <StatusPill tone={STATUS_TONE[alert?.status] || 'neutral'}>
            {`${alert?.alertType?.category || ''} Bot Alert · ${alert?.status || ''}`}
          </StatusPill>
          {alert?.createdAt && (
            <span className="inline-flex items-center gap-1 text-xs text-ink-500 ml-auto">
              <Calendar size={14} />
              {formatDate(alert.createdAt)}
            </span>
          )}
        </div>

        {/* Member */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ backgroundColor: '#e8eef7', color: '#4062E5', fontSize: 16, fontWeight: 600 }}>
            {u?.profilePicture ? <img src={u.profilePicture} alt="" className="w-full h-full object-cover" /> : initials(u)}
          </div>
          <div className="min-w-0">
            <h5 className="text-base font-semibold text-ink-900 mb-1 truncate">{u?.fullName || `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || '—'}</h5>
            <div className="flex gap-2">
              <Link to={`/members/alerts/${alert?.memberId}`} className="inline-flex items-center px-2.5 py-1 rounded-[6px] text-xs font-medium text-ink-900 bg-white border border-rule hover:border-brand-500 hover:text-brand-700 transition" style={{ borderStyle: 'solid' }}>
                View Alerts
              </Link>
              <Link to={`/members/activity/view?member=${alert?.memberId}`} className="inline-flex items-center px-2.5 py-1 rounded-[6px] text-xs font-medium text-ink-900 bg-white border border-rule hover:border-brand-500 hover:text-brand-700 transition" style={{ borderStyle: 'solid' }}>
                View Engagement
              </Link>
            </div>
          </div>
        </div>

        <hr className="border-0 mb-4" style={{ height: 1, background: '#ececf0' }} />

        {/* Description (shared partial) + action */}
        <div className="mb-3">
          <AlertDescription alert={alert} />
        </div>
        <div className="flex gap-2 mb-1">
          <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 8, background: '#FCECE7' }}>
            <AlertTriangle size={22} color="#EB6240" />
          </div>
          <div>
            <div className="text-sm text-ink-500 mb-0.5">Alert Action</div>
            <div className="text-sm font-medium text-ink-900">{alertActionText(alert?.status)}</div>
          </div>
        </div>

        {/* Footer actions */}
        {alert?.status === 'Resolved' && alert?.acknowledgements?.[0]?.acknowledgementUndertaken?.[0] && (
          <div className="mt-5">
            <a href={alert.acknowledgements[0].acknowledgementUndertaken} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition" style={{ borderStyle: 'solid' }}>
              <ExternalLink size={15} />
              View Acknowledgement
            </a>
          </div>
        )}
        {alert?.status === 'AcknowledgementSent' && (
          <div className="mt-5">
            <button type="button" className="inline-flex items-center gap-1.5 px-4 py-2 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition" style={{ borderStyle: 'solid' }}>
              <RefreshCw size={15} />
              Resend Acknowledgement
            </button>
          </div>
        )}
        {alert?.status === 'Pending' && (
          <div className="mt-5 flex items-center gap-2">
            <button
              type="button"
              disabled={isUpdating}
              onClick={handleSendAcknowledgement}
              className="inline-flex items-center justify-center px-4 py-2 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {isUpdating ? 'Updating…' : 'Send Acknowledgement'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex items-center justify-center px-4 py-2 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition"
              style={{ borderStyle: 'solid' }}
            >
              Close
            </button>
          </div>
        )}
      </Box>
    </Modal>
  );
}

ViewIssueModal.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  selectedAlert: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default ViewIssueModal;
